/**
 * BotSpot Web Dashboard - Agent Steps SSE Hook
 *
 * React hook for real-time agent step streaming via Server-Sent Events.
 * Connects to the API gateway SSE proxy endpoint and parses agent_step events
 * into typed state for rendering in the BotSteps component.
 */

import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Agent step event from the SSE stream.
 *
 * Each step has a "step" event followed by a "step_result" event.
 * Both share the same step_number for correlation.
 */
export interface AgentStep {
  type: "step" | "step_result";
  step_number: number;
  action?: string;
  target?: string;
  reasoning?: string;
  outcome?: string;
  duration_ms?: number;
  error_message?: string;
  screenshot_ref?: string;
  ts: string;
}

/** Maximum number of step events to keep in memory */
const MAX_STEPS = 500;

/**
 * Hook for subscribing to real-time agent step events via SSE.
 *
 * Creates an EventSource connection to the API gateway SSE proxy endpoint
 * and parses incoming agent_step events into a typed array.
 *
 * @param botId - Bot ID to subscribe to (null to disable)
 * @param enabled - Whether the connection should be active
 * @returns Object with steps array, connection status, and clear function
 *
 * @example
 * const { steps, connected, clearSteps } = useAgentSteps(botId, bot?.status === BotStatus.RUNNING);
 */
export function useAgentSteps(
  botId: string | null,
  enabled: boolean
): {
  steps: AgentStep[];
  connected: boolean;
  clearSteps: () => void;
} {
  const [steps, setSteps] = useState<AgentStep[]>([]);
  const [connected, setConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);

  /**
   * Clear all accumulated steps
   */
  const clearSteps = useCallback(() => {
    setSteps([]);
  }, []);

  useEffect(() => {
    // Do not connect if no botId or not enabled
    if (!botId || !enabled) {
      // Close any existing connection when disabled
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
        setConnected(false);
      }
      return;
    }

    // Construct SSE URL (Vite proxy handles routing to API gateway)
    const url = `/api/v1/bots/${botId}/steps/stream`;

    const es = new EventSource(url);
    eventSourceRef.current = es;

    // Handle connection open
    es.onopen = () => {
      setConnected(true);
    };

    // Listen for agent_step events
    es.addEventListener("agent_step", (event: MessageEvent) => {
      try {
        const step: AgentStep = JSON.parse(event.data);
        setSteps((prev) => {
          const next = [...prev, step];
          // Cap at MAX_STEPS to prevent unbounded memory growth
          if (next.length > MAX_STEPS) {
            return next.slice(next.length - MAX_STEPS);
          }
          return next;
        });
      } catch {
        // Silently ignore malformed events
      }
    });

    // Listen for ping events (no-op, keeps connection alive)
    es.addEventListener("ping", () => {
      // Keepalive - no action needed
    });

    // Handle errors (EventSource auto-reconnects by default)
    es.onerror = () => {
      setConnected(false);
    };

    // Cleanup on unmount or when botId/enabled changes
    return () => {
      es.close();
      eventSourceRef.current = null;
      setConnected(false);
    };
  }, [botId, enabled]);

  return { steps, connected, clearSteps };
}
